import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  InputBase,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Search,
  Close,
  Tv,
  Movie as MovieIcon,
  Layers,
  CalendarToday,
  PlayCircle,
} from '@mui/icons-material';
import { useLazyQuery } from '@apollo/client';
import {
  SearchTagTvProgramsQuery,
  SearchTagTvProgramsQuery_query_searchTagTvPrograms_TvSearchProgramTvResult,
  SearchTagTvProgramsQueryVariables,
  TagType,
  TvSearchContextType,
} from '../../../../__generated__/queries-web';
import { SEARCH_TAG_TV_PROGRAMS } from '../../../../apollo/queries/tagtvprogram.web.graphql';
import { useEditableTag } from '../../useEditTag';
import {
  iconContainerStyles,
  itemContainerStyles,
  sectionHeaderStyles,
  contentContainerStyles,
  titleTextStyles,
  secondaryTextStyles,
  separatorStyles,
  overflowTextStyles,
} from './style';

export const SearchDropdown = ({
  handleAddTag,
  searchContext,
}: {
  handleAddTag: ReturnType<typeof useEditableTag>['handlers']['handleAddTag'];
  searchContext: TvSearchContextType;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [
    searchResults,
    setSearchResults,
  ] = useState<SearchTagTvProgramsQuery_query_searchTagTvPrograms_TvSearchProgramTvResult | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const [searchTagTvProgram] = useLazyQuery<
    SearchTagTvProgramsQuery,
    SearchTagTvProgramsQueryVariables
  >(SEARCH_TAG_TV_PROGRAMS, {
    fetchPolicy: 'no-cache',
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setSearchResults(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const { data: searchData } = await searchTagTvProgram({
          variables: {
            query: searchQuery.trim(),
            context: searchContext,
          },
        });

        if (searchData?.searchTagTvPrograms) {
          setSearchResults(searchData.searchTagTvPrograms);
        }
      } catch (error) {
        console.error('Error searching TV programs:', error);
        setSearchResults(null);
      } finally {
        setIsLoading(false);
      }
    },
    [searchTagTvProgram, searchContext],
  );

  // Debounced search with useEffect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // eslint-disable-next-line
    timeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, performSearch]);

  const hasResults =
    searchResults &&
    Object.values(searchResults).some((arr: any) => arr.length > 0);

  const handleFocus = () => setIsOpen(true);

  const handleClear = () => {
    setQuery('');
    setIsOpen(true);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        maxWidth: '720px',
        mx: 'auto',
        position: 'relative',
        zIndex: 50,
      }}
    >
      {/* Material Design 3 Search Bar */}
      <Box sx={{ position: 'relative', transition: 'all 0.2s' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            pl: 2,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Search sx={{ fontSize: 24 }} />
        </Box>
        <InputBase
          type="text"
          placeholder="Rechercher un épisode, une série, un film, etc..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          sx={{
            width: '100%',
            pl: '56px',
            pr: '48px',
            height: '56px',
            bgcolor: 'grey.100',
            color: 'text.primary',
            borderRadius: '28px',
            fontSize: '1rem',
            transition: 'all 0.2s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            '&:hover': {
              bgcolor: 'grey.200',
            },
            '&.Mui-focused': {
              bgcolor: 'grey.200',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            },
            '& input::placeholder': {
              color: 'text.secondary',
              opacity: 1,
            },
          }}
        />
        {query && (
          <IconButton
            onClick={handleClear}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              pr: 2,
              borderRadius: '28px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'transparent',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                p: 0.5,
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Close sx={{ fontSize: 24, color: 'text.secondary' }} />
            </Box>
          </IconButton>
        )}
      </Box>

      {/* Material Dropdown Surface - Condensed */}
      {isOpen && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            mt: 1,
            width: '100%',
            // bgcolor: '#F7F2FA',
            borderRadius: '16px',
            boxShadow: '0 4px 8px 3px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxHeight: '70vh',
            overflowY: 'auto',
            py: 1,
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
              }}
            >
              <CircularProgress size={32} sx={{ color: '#ef5350' }} />
            </Box>
          ) : !hasResults ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {query
                  ? `Aucun résultat trouvé pour "${query}"`
                  : 'Commencez à taper pour rechercher...'}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Broadcasts Section */}
              {searchResults.broadcasts.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography component="h3" sx={sectionHeaderStyles}>
                    Broadcasts
                  </Typography>
                  {searchResults.broadcasts.map((item) => {
                    const date = new Date(item.startedAt);
                    const timeStr = date.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const dateStr = date.toLocaleDateString([], {
                      month: 'short',
                      day: 'numeric',
                    });

                    return (
                      <Box
                        key={item.boneUUID}
                        onClick={() => {
                          handleAddTag({
                            id: item.boneUUID,
                            title: `${item.title} - ${item.channel.title} - ${dateStr}, ${timeStr}`,
                            type: TagType.TvProgram,
                            boneUUID: item.program.boneUUID,
                            relationId: item.program.boneUUID,
                            country: item.program.country,
                          });
                        }}
                        sx={itemContainerStyles}
                      >
                        <Box sx={{ mr: 1.5, ...iconContainerStyles }}>
                          <Tv sx={{ fontSize: 16 }} />
                        </Box>
                        <Box sx={contentContainerStyles}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'baseline',
                            }}
                          >
                            <Typography sx={titleTextStyles}>
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.6875rem',
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                ml: 1,
                                opacity: 0.8,
                              }}
                            >
                              {dateStr}, {timeStr}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              lineHeight: 1.25,
                              mt: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 500,
                                color: '#ef5350',
                                fontSize: 'inherit',
                              }}
                            >
                              {item.channel.title}
                            </Typography>
                            {item.program.collectionItemTitle && (
                              <>
                                <Typography
                                  component="span"
                                  sx={{
                                    ...separatorStyles,
                                    mx: 0.75,
                                    fontSize: 'inherit',
                                  }}
                                >
                                  •
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    ...overflowTextStyles,
                                    fontSize: 'inherit',
                                  }}
                                >
                                  {item.program.collectionItemTitle}
                                </Typography>
                              </>
                            )}
                            {item.program.country && (
                              <>
                                <Typography
                                  component="span"
                                  sx={{
                                    ...separatorStyles,
                                    mx: 0.75,
                                    fontSize: 'inherit',
                                  }}
                                >
                                  •
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    ...overflowTextStyles,
                                    fontSize: 'inherit',
                                  }}
                                >
                                  {item.program.country}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Movies Section */}
              {searchResults.movies.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography component="h3" sx={sectionHeaderStyles}>
                    Movies
                  </Typography>
                  {searchResults.movies.map((item) => (
                    <Box
                      key={item.boneUUID}
                      onClick={() => {
                        handleAddTag({
                          id: item.boneUUID,
                          title: item.releasedYear
                            ? `${item.title} - ${item.releasedYear}`
                            : item.title,
                          type: TagType.TvProgram,
                          boneUUID: item.boneUUID,
                          relationId: item.boneUUID,
                          country: item.country,
                        });
                      }}
                      sx={itemContainerStyles}
                    >
                      <Box sx={{ mr: 1.5, ...iconContainerStyles }}>
                        <MovieIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={contentContainerStyles}>
                        <Typography sx={titleTextStyles}>
                          {item.title}
                        </Typography>
                        <Typography sx={secondaryTextStyles}>
                          Movie{' '}
                          <Typography component="span" sx={separatorStyles}>
                            •
                          </Typography>{' '}
                          {item.releasedYear}
                          {item.country && (
                            <>
                              {' '}
                              <Typography component="span" sx={separatorStyles}>
                                •
                              </Typography>{' '}
                              {item.country}
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Series Section */}
              {searchResults.series.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography component="h3" sx={sectionHeaderStyles}>
                    TV Shows
                  </Typography>
                  {searchResults.series.map((item) => (
                    <Box
                      key={item.boneUUID}
                      onClick={() => {
                        handleAddTag({
                          id: item.boneUUID,
                          title: item.releasedYear
                            ? `${item.title} - ${item.releasedYear}`
                            : item.title,
                          type: TagType.TvCollection,
                          boneUUID: item.boneUUID,
                          relationId: item.boneUUID,
                          country: item.country,
                        });
                      }}
                      sx={itemContainerStyles}
                    >
                      <Box sx={{ mr: 1.5, ...iconContainerStyles }}>
                        <Layers sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={contentContainerStyles}>
                        <Typography sx={titleTextStyles}>
                          {item.title}
                        </Typography>
                        <Typography sx={secondaryTextStyles}>
                          TV Series{' '}
                          {item.releasedYear && (
                            <>
                              <Typography component="span" sx={separatorStyles}>
                                •
                              </Typography>{' '}
                              {item.releasedYear}
                            </>
                          )}
                          {item.country && (
                            <>
                              {' '}
                              <Typography component="span" sx={separatorStyles}>
                                •
                              </Typography>{' '}
                              {item.country}
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Seasons Section */}
              {searchResults.seasons.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography component="h3" sx={sectionHeaderStyles}>
                    Seasons
                  </Typography>
                  {searchResults.seasons.map((item) => {
                    const seriesTitle = item.parentCollection.title;
                    const seasonLabel = item.itemIndex
                      ? `Season ${item.itemIndex}`
                      : 'Special Season';
                    const countryValue =
                      item.country || item.parentCollection.country;

                    return (
                      <Box
                        key={`season-${item.boneUUID}`}
                        onClick={() => {
                          handleAddTag({
                            id: item.boneUUID,
                            title: item.releasedYear
                              ? `${seriesTitle} - ${seasonLabel} - ${item.releasedYear}`
                              : `${seriesTitle} - ${seasonLabel}`,
                            type: TagType.TvCollection,
                            boneUUID: item.boneUUID,
                            relationId: item.boneUUID,
                            country: countryValue,
                          });
                        }}
                        sx={itemContainerStyles}
                      >
                        <Box sx={{ mr: 1.5, ...iconContainerStyles }}>
                          <CalendarToday sx={{ fontSize: 16 }} />
                        </Box>
                        <Box sx={contentContainerStyles}>
                          <Typography sx={titleTextStyles}>
                            {seriesTitle}
                          </Typography>
                          <Typography sx={secondaryTextStyles}>
                            {seasonLabel}{' '}
                            <Typography component="span" sx={separatorStyles}>
                              •
                            </Typography>{' '}
                            {item.releasedYear}
                            {countryValue && (
                              <>
                                {' '}
                                <Typography
                                  component="span"
                                  sx={separatorStyles}
                                >
                                  •
                                </Typography>{' '}
                                {countryValue}
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Episodes Section */}
              {searchResults.episodes.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography component="h3" sx={sectionHeaderStyles}>
                    Episodes
                  </Typography>
                  {searchResults.episodes.map((item) => {
                    const seriesTitle = item.collection.parentCollection?.title;
                    const seasonNum = item.collection.itemIndex;
                    const episodeNum = item.collectionItemIndex;
                    const epTitle = item.collectionItemTitle || item.title;
                    const countryValue =
                      item.country ||
                      item.collection.country ||
                      item.collection.parentCollection?.country;

                    const metaString =
                      seasonNum && episodeNum
                        ? `S${seasonNum} E${episodeNum}`
                        : 'Episode';

                    return (
                      <Box
                        key={`episode-${item.boneUUID}`}
                        onClick={() => {
                          handleAddTag({
                            id: item.boneUUID,
                            title: seriesTitle
                              ? `${item.title} - ${seriesTitle} - ${metaString}`
                              : `${item.title} - ${metaString}`,
                            type: TagType.TvCollection,
                            boneUUID: item.boneUUID,
                            relationId: item.boneUUID,
                            country: countryValue,
                          });
                        }}
                        sx={itemContainerStyles}
                      >
                        <Box sx={{ mr: 1.5, ...iconContainerStyles }}>
                          <PlayCircle sx={{ fontSize: 16 }} />
                        </Box>
                        <Box sx={contentContainerStyles}>
                          <Typography sx={titleTextStyles}>
                            {epTitle}
                          </Typography>
                          <Typography
                            sx={
                              {
                                ...secondaryTextStyles,
                                ...overflowTextStyles,
                              } as SxProps<Theme>
                            }
                          >
                            {seriesTitle}{' '}
                            <Typography component="span" sx={separatorStyles}>
                              •
                            </Typography>{' '}
                            {metaString}
                            {countryValue && (
                              <>
                                {' '}
                                <Typography
                                  component="span"
                                  sx={separatorStyles}
                                >
                                  •
                                </Typography>{' '}
                                {countryValue}
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};
